import React, { useState } from 'react'
import { AnalysisSummary as AnalysisItem } from '../../../services/api'
import { CRIT, STATUS, TAG_LABEL, countryMeta, daysUntil, fmtDate, timeAgo, isRecent } from './helpers'
import { Lozenge, Dot, SmallTag, CountryChip } from './atoms'

export function AnalysisCard({
  item, onClick, onQuickStatus,
}: {
  item: AnalysisItem
  onClick: (id: number) => void
  onQuickStatus: (id: number, status: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [quickOpen, setQuickOpen] = useState(false)
  const crit = CRIT[item.criticality] ?? CRIT.medium
  const stat = STATUS[item.review_status] ?? STATUS.pending
  const days = daysUntil(item.compliance_deadline)
  const overdue = days < 0, urgent = days >= 0 && days <= 90
  const deadlineColor = overdue ? 'var(--an-red)' : urgent ? 'var(--an-amber)' : 'var(--an-ink-3)'

  return (
    <div className="bg-an-surface border border-an-line hover:border-an-line-mid rounded-card overflow-hidden transition-all shadow-card hover:shadow-card-hover">
      <div className="grid grid-cols-[3px_1fr] cursor-pointer" onClick={() => onClick(item.id)}>
        <div style={{ background: crit.c, opacity: 0.55 }} />
        <div className="grid grid-cols-[1fr_auto]">
          <div className="pt-[18px] pr-5 pb-4 pl-[22px] flex flex-col gap-[9px] min-w-0">
            <div className="flex gap-[5px] items-center flex-wrap">
              <Dot c={crit.c} size={7} />
              <span className="text-[11px] font-semibold tracking-[0.01em]" style={{ color: crit.c }}>{crit.label}</span>
              <span className="text-xs mx-0.5 text-an-line-mid">&middot;</span>
              <Lozenge c={stat.c} bg={stat.bg}>{stat.label}</Lozenge>
              {item.reviewed_by && item.review_status !== 'pending' && (
                <span className="text-[10.5px] text-an-ink-3 italic">by {item.reviewed_by.split('@')[0]}</span>
              )}
              {item.requires_action && <Lozenge c="var(--an-red)" bg="var(--an-red-bg)">Action required</Lozenge>}
              {item.flagged_for_review && <span className="text-[13px] text-an-amber">{'⚑'}</span>}
              <span className="ml-auto flex items-center gap-2">
                {isRecent(item.analysed_at) && <Lozenge c="var(--an-accent)" bg="var(--an-accent-dim)">New</Lozenge>}
                <span className="text-[10.5px] text-an-ink-3 tabular-nums">{timeAgo(item.analysed_at)}</span>
                <CountryChip code={item.country} sm />
              </span>
            </div>
            <div className="text-[15.5px] font-[650] text-an-ink leading-tight tracking-[-0.025em]">{item.title}</div>
            <div className="text-[13px] text-an-ink-2 leading-relaxed line-clamp-2">{item.one_liner}</div>
            <div className="flex items-center gap-1.5 flex-wrap mt-px">
              {item.relevant_to.slice(0, 3).map(t => <SmallTag key={t}>{TAG_LABEL[t] || t}</SmallTag>)}
              <button
                onClick={e => { e.stopPropagation(); setExpanded(x => !x) }}
                className="ml-auto text-[11px] px-2 py-0.5 rounded-[5px] border border-an-line-mid bg-transparent text-an-ink-3 cursor-pointer flex items-center gap-[3px] shrink-0"
              >
                <span className={`inline-block text-[8px] transition-transform ${expanded ? 'rotate-180' : ''}`}>{'▾'}</span>
                {expanded ? 'Hide' : 'Provisions'}
              </button>
            </div>
          </div>
          <div className="w-[150px] shrink-0 border-l border-an-line bg-an-surface-2 flex flex-col items-end justify-between px-[18px] pt-[18px] pb-3.5">
            <div>
              <div className="text-[9px] font-semibold tracking-[0.09em] uppercase text-an-ink-3 mb-[5px] text-right">Deadline</div>
              <div className="text-[13px] font-bold font-mono whitespace-nowrap text-right tracking-[-0.01em]" style={{ color: deadlineColor }}>{fmtDate(item.compliance_deadline)}</div>
              {(overdue || urgent) && <div className="text-[10px] font-medium text-right mt-0.5" style={{ color: deadlineColor }}>{overdue ? 'Past deadline' : `${days}d remaining`}</div>}
            </div>
            {item.review_status === 'pending' && (
              <button
                onClick={e => { e.stopPropagation(); setQuickOpen(q => !q) }}
                className="text-[11px] px-[9px] py-[3px] rounded-[5px] border border-an-line-mid bg-transparent text-an-ink-3 cursor-pointer whitespace-nowrap"
              >
                Review {'▾'}
              </button>
            )}
          </div>
        </div>
      </div>

      {quickOpen && item.review_status === 'pending' && (
        <div onClick={e => e.stopPropagation()} className="border-t border-an-line py-[9px] pr-[18px] pl-[25px] bg-an-surface-2 flex gap-1.5 items-center animate-[an-in_0.12s_ease]">
          <span className="text-[10px] text-an-ink-3 font-semibold tracking-[0.05em] uppercase mr-0.5">Mark as</span>
          {(['accepted', 'rejected', 'dismissed'] as const).map(s => {
            const st = STATUS[s]
            return (
              <button
                key={s}
                onClick={() => { onQuickStatus(item.id, s); setQuickOpen(false) }}
                className="px-[11px] py-[3px] rounded-[5px] text-xs font-semibold cursor-pointer"
                style={{ border: `1px solid ${st.c}25`, background: st.bg, color: st.c }}
              >
                {st.label}
              </button>
            )
          })}
          <button onClick={() => setQuickOpen(false)} className="ml-auto text-[11px] text-an-ink-3 bg-transparent border-none cursor-pointer p-1">{'✕'}</button>
        </div>
      )}

      {expanded && (
        <div className="border-t border-an-line px-6 py-3.5 bg-an-surface-2 animate-[an-in_0.12s_ease]">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-an-ink-3 mb-[9px]">Key Provisions</div>
          <div className="grid grid-cols-2 gap-[5px_20px]">
            {item.key_provisions.map((p, i) => (
              <div key={i} className="flex gap-[7px] text-[12.5px] text-an-ink-2 leading-relaxed">
                <span className="text-an-accent shrink-0 mt-px text-sm leading-none">{'›'}</span>{p}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function CompactRow({ item, onClick }: { item: AnalysisItem; onClick: (id: number) => void }) {
  const crit = CRIT[item.criticality] ?? CRIT.medium
  const stat = STATUS[item.review_status] ?? STATUS.pending
  const days = daysUntil(item.compliance_deadline)
  const overdue = days < 0, urgent = days >= 0 && days <= 90
  const deadlineColor = overdue ? 'var(--an-red)' : urgent ? 'var(--an-amber)' : 'var(--an-ink-3)'

  return (
    <div
      onClick={() => onClick(item.id)}
      className="grid grid-cols-[3px_1fr_auto] cursor-pointer hover:bg-an-surface-2 border-b border-an-line transition-colors"
    >
      <div className="rounded-l-sm" style={{ background: crit.c, opacity: 0.4 }} />
      <div className="py-[9px] pr-3.5 pl-4 flex items-center gap-2 min-w-0">
        <Dot c={crit.c} size={6} />
        <span className="text-[13px] font-[590] text-an-ink tracking-[-0.02em] overflow-hidden text-ellipsis whitespace-nowrap flex-1">{item.title}</span>
        <div className="flex gap-1 shrink-0 items-center">
          <span className="text-xs" title={countryMeta(item.country).label}>{countryMeta(item.country).flag || countryMeta(item.country).label}</span>
          <Lozenge c={stat.c} bg={stat.bg} sm>{stat.label}</Lozenge>
          {item.requires_action && <Lozenge c="var(--an-red)" bg="var(--an-red-bg)" sm>Action</Lozenge>}
          {item.flagged_for_review && <span className="text-[11px] text-an-amber">{'⚑'}</span>}
        </div>
      </div>
      <div className="py-[9px] pr-4 flex items-center gap-3 shrink-0">
        <span className="text-[10.5px] text-an-ink-3 tabular-nums">{timeAgo(item.analysed_at)}</span>
        <div className="text-right">
          <div className="text-[11.5px] font-semibold font-mono whitespace-nowrap" style={{ color: deadlineColor }}>{fmtDate(item.compliance_deadline)}</div>
          {(overdue || urgent) && <div className="text-[9.5px] mt-px font-medium" style={{ color: deadlineColor }}>{overdue ? 'Overdue' : `${days}d`}</div>}
        </div>
      </div>
    </div>
  )
}
